import { saveForm as saveFormRequest, IValues as IValuesSaveForm } from '../../api/deal_rent/save-form';
import {
  sendValidationCode as sendValidationCodeApi,
  IValues as IValuesSendCode,
} from '../../api/deal_rent/send-validation-code';
import { prefetchFormData as prefetchFormDataApi } from '../../api/deal_rent/prefetch-form-data';
import { IThunkActionCreator } from '../index';
import * as R from 'ramda';
import { trackSafeRentClick, trackSafeRentFormSentSuccess } from './tracking';

export interface IOpen {
  type: 'deal-rent/open';
}
export interface IClose {
  type: 'deal-rent/close';
}
export interface IOpenSuccess {
  type: 'deal-rent/openSuccess';
}
export interface ICloseSuccess {
  type: 'deal-rent/closeSuccess';
}
export interface IOpenFailure {
  type: 'deal-rent/openFailure';
}
export interface ICloseFailure {
  type: 'deal-rent/closeFailure';
}
export interface ISaveFormStart {
  type: 'deal-rent/saveFormStart';
}
export interface ISaveFormSuccess {
  type: 'deal-rent/saveFormSuccess';
}
export interface ISaveFormError {
  type: 'deal-rent/saveFormError';
  payload: string;
}
export interface IPrefetchFormStateStart {
  type: 'deal-rent/prefetchFormStateStart';
}
export interface IPrefetchFormStateSuccess {
  type: 'deal-rent/prefetchFormStateSuccess';
  payload: {
    form: IValuesSaveForm;
    phoneConfirmed: boolean;
  };
}
export interface IPrefetchFormStateError {
  type: 'deal-rent/prefetchFormStateError';
}
export interface ISendSMSStart {
  type: 'deal-rent/sendSMSStart';
}
export interface ISendSMSSuccess {
  type: 'deal-rent/sendSMSSuccess';
  payload: number;
}
export interface ISendSMSAlreadyConfirmed {
  type: 'deal-rent/sendSMSAlreadyConfirmed';
}
export interface ISendSMSError {
  type: 'deal-rent/sendSMSError';
  payload: string;
}
export interface IChangePhoneConfirmed {
  type: 'deal-rent/changePhoneConfirmed';
  payload: boolean;
}

export type actions =
  | IOpen
  | IClose
  | IOpenSuccess
  | ICloseSuccess
  | IOpenFailure
  | ICloseFailure
  | ISaveFormStart
  | ISaveFormSuccess
  | ISaveFormError
  | IPrefetchFormStateStart
  | IPrefetchFormStateSuccess
  | IPrefetchFormStateError
  | ISendSMSStart
  | ISendSMSSuccess
  | ISendSMSError
  | ISendSMSAlreadyConfirmed
  | IChangePhoneConfirmed;

export const open = (): IOpen => ({
  type: 'deal-rent/open',
});
export const close = (): IClose => ({
  type: 'deal-rent/close',
});
export const openSuccess = (): IOpenSuccess => ({
  type: 'deal-rent/openSuccess',
});
export const closeSuccess = (): ICloseSuccess => ({
  type: 'deal-rent/closeSuccess',
});
export const openFailure = (): IOpenFailure => ({
  type: 'deal-rent/openFailure',
});
export const closeFailure = (): ICloseFailure => ({
  type: 'deal-rent/closeFailure',
});
export const saveFormStart = (): ISaveFormStart => ({
  type: 'deal-rent/saveFormStart',
});
export const saveFormSuccess = (): ISaveFormSuccess => ({
  type: 'deal-rent/saveFormSuccess',
});
export const saveFormError = (message: string): ISaveFormError => ({
  type: 'deal-rent/saveFormError',
  payload: message,
});
export const prefetchFormStateStart = (): IPrefetchFormStateStart => ({
  type: 'deal-rent/prefetchFormStateStart',
});
export const prefetchFormStateSuccess = (values: {
  form: IValuesSaveForm;
  phoneConfirmed: boolean;
}): IPrefetchFormStateSuccess => ({
  type: 'deal-rent/prefetchFormStateSuccess',
  payload: values,
});
export const prefetchFormStateError = (): IPrefetchFormStateError => ({
  type: 'deal-rent/prefetchFormStateError',
});
export const sendSMSStart = (): ISendSMSStart => ({
  type: 'deal-rent/sendSMSStart',
});
export const sendSMSSuccess = (seconds: number): ISendSMSSuccess => ({
  type: 'deal-rent/sendSMSSuccess',
  payload: seconds,
});
export const sendSMSError = (error: string): ISendSMSError => ({
  type: 'deal-rent/sendSMSError',
  payload: error,
});
export const sendSMSAlreadyConfirmed = (): ISendSMSAlreadyConfirmed => ({
  type: 'deal-rent/sendSMSAlreadyConfirmed',
});
export const changePhoneConfirmed = (value: boolean): IChangePhoneConfirmed => ({
  type: 'deal-rent/changePhoneConfirmed',
  payload: value,
});

export const saveForm = (values: IValuesSaveForm): IThunkActionCreator => async (dispatch, getState, context) => {
  dispatch(saveFormStart());

  const dealRentAnnouncementsBrowserUrl = context.config.get('dealRentAnnouncements.browserUrl') as string;

  try {
    await saveFormRequest(context.makeRequest, dealRentAnnouncementsBrowserUrl, {
      ...values,
      objectId: getState().offerData.offer.id,
    });
    dispatch(saveFormSuccess());
    dispatch(close());
    dispatch(openSuccess());
    trackSafeRentFormSentSuccess();
  } catch (error) {
    dispatch(saveFormError(error.message));
    if (R.pathOr([], ['errors'], error).find(({ code }) => code === 'FormAlreadySent')) {
      dispatch(close());
      dispatch(openFailure());
    }
  }
};

export const sendValidationCode = (values: IValuesSendCode): IThunkActionCreator => async (
  dispatch,
  getState,
  context,
) => {
  const dealRentAnnouncementsBrowserUrl = context.config.get('dealRentAnnouncements.browserUrl') as string;
  dispatch(sendSMSStart());

  try {
    const result = await sendValidationCodeApi(context.makeRequest, dealRentAnnouncementsBrowserUrl, values);
    dispatch(sendSMSSuccess(result.secondsToRetry));
  } catch (error) {
    if (R.pathOr([], ['errors'], error).find(({ code }) => code === 'NoConfirmationRequired')) {
      dispatch(sendSMSAlreadyConfirmed());
    } else {
      dispatch(sendSMSError(error.message));
    }
  }
};

export const prefetchFormState = (): IThunkActionCreator => async (dispatch, getState, context) => {
  dispatch(prefetchFormStateStart());
  trackSafeRentClick();

  const dealRentAnnouncementsBrowserUrl = context.config.get('dealRentAnnouncements.browserUrl') as string;

  try {
    const result = await prefetchFormDataApi(context.makeRequest, dealRentAnnouncementsBrowserUrl);
    dispatch(
      prefetchFormStateSuccess({ ...result, form: { ...result.form, phone: result.form.phone.replace('+7', '') } }),
    );
  } catch (error) {
    dispatch(prefetchFormStateError());
  } finally {
    dispatch(open());
  }
};
